import React, { useEffect, useState } from 'react'
import { Button, Select } from 'components/ui'

/**
 * Where a payout goes. `value` / `onChange` carry the channel CODE the
 * backend expects ('' when nothing is picked yet).
 *
 * When the backend returns typed destinations (Gateway 2), this is the
 * owner's fixed layout: Bank / GCash / Maya buttons, GCash and Maya picking
 * their single code outright and Bank opening a dropdown of the allowed
 * banks. The list and its order come from the server, which also rejects
 * anything not on it. An untyped list (Gateway 1) keeps the old single
 * searchable dropdown.
 */
const PayoutChannelPicker = ({ channels, value, onChange, disabled, isLoading }) => {
    const typed = channels.some((c) => c.type)
    const ewallets = channels.filter((c) => c.type === 'EWALLET')
    const banks = channels.filter((c) => c.type === 'BANK')

    // 'BANK', an e-wallet key ('GCASH', 'MAYA'), or null. Kept separately from
    // `value` because choosing Bank has no code until a bank is picked.
    const [mode, setMode] = useState(null)

    // Follow `value` when it changes from outside (a cleared form clears the
    // e-wallet buttons; Bank stays open, since picking Bank itself sets '').
    useEffect(() => {
        if (!value) {
            setMode((m) => (m === 'BANK' ? m : null))
            return
        }
        const wallet = ewallets.find((c) => c.code === value)
        setMode(wallet ? wallet.key : 'BANK')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, channels])

    if (!typed) {
        const options = channels.map((c) => ({ value: c.code, label: c.name }))
        return (
            <Select
                placeholder="Select a bank or e-wallet"
                isLoading={isLoading}
                isDisabled={disabled || channels.length === 0}
                options={options}
                value={options.filter((o) => o.value === value)}
                onChange={(selected) => onChange(selected.value)}
            />
        )
    }

    const bankOptions = banks.map((c) => ({ value: c.code, label: c.name }))

    const pick = (nextMode) => {
        setMode(nextMode)
        if (nextMode === 'BANK') {
            onChange('')
        } else {
            onChange(ewallets.find((c) => c.key === nextMode).code)
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-2">
                {banks.length > 0 && (
                    <Button
                        type="button"
                        size="sm"
                        variant={mode === 'BANK' ? 'solid' : 'default'}
                        disabled={disabled}
                        onClick={() => pick('BANK')}
                    >
                        Bank
                    </Button>
                )}
                {ewallets.map((c) => (
                    <Button
                        key={c.key}
                        type="button"
                        size="sm"
                        variant={mode === c.key ? 'solid' : 'default'}
                        disabled={disabled}
                        onClick={() => pick(c.key)}
                    >
                        {c.name}
                    </Button>
                ))}
            </div>
            {mode === 'BANK' && (
                <Select
                    placeholder="Select your bank"
                    isDisabled={disabled}
                    options={bankOptions}
                    value={bankOptions.filter((o) => o.value === value)}
                    onChange={(selected) => onChange(selected.value)}
                />
            )}
        </div>
    )
}

export default PayoutChannelPicker
